import {
  renderPatientTrackingDashboardCard,
  type PatientTrackingDashboardSummary
} from '@jarvis/platform-ui/patient-tracking-dashboard';
import {
  DashboardHeaderActions,
  DashboardLayoutControls,
  DashboardLoadingState,
  DashboardSortableGrid,
  renderStorageDashboardCard,
  useDashboardLayout,
  type StorageDashboardSummary
} from '@jarvis/platform-ui/storage-dashboard';
import {
  CalculatorWidget,
  MyAppsShortcutsCard,
  MY_APPS_SHORTCUTS_CARD_ID,
  TipsAndTricksCard,
  TIPS_AND_TRICKS_CARD_ID,
  WEBOPS_MY_APPS_REGISTRY
} from '@jarvis/platform-ui';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchStorageDashboardSummary } from '../../api/filesDashboard';
import { useHasRole } from '../../auth/useHasRole';
import { useAuthStore } from '../../auth/useAuthStore';
import { fetchPatientTrackingDashboardSummary } from '../../api/patientTrackingDashboard';
import { fetchTimeOffDashboardSummary, type TimeOffDashboardSummary } from '../../api/timeOff';
import {
  CALCULATOR_CARD_ID,
  DEFAULT_STORAGE_CARD_ORDER,
  MY_APPS_DASHBOARD_CARDS,
  PHARMACY_TRACKING_DASHBOARD_CARDS,
  STORAGE_DASHBOARD_CARD_CONFIGS,
  TIME_OFF_DASHBOARD_CARDS,
  TIPS_DASHBOARD_CARD,
  type DashboardCardConfig,
  type DashboardCardId
} from './components/DashboardLayoutControls';
import { TimeOffAttentionCard } from './components/TimeOffAttentionCard';
import { TimeOffSyncHealthCard } from './components/TimeOffSyncHealthCard';
import { TimeOffTodayCard } from './components/TimeOffTodayCard';

const STORAGE_PREFIX = 'jarvis-webops-dashboard-layout-v2';

function cardsForUser(
  canSeeTimeOff: boolean,
  canSeeStorage: boolean,
  canSeePharmacyTracking: boolean
): DashboardCardConfig[] {
  return [
    TIPS_DASHBOARD_CARD,
    ...MY_APPS_DASHBOARD_CARDS,
    ...(canSeeTimeOff ? TIME_OFF_DASHBOARD_CARDS : []),
    ...(canSeeStorage ? STORAGE_DASHBOARD_CARD_CONFIGS : []),
    ...(canSeePharmacyTracking ? PHARMACY_TRACKING_DASHBOARD_CARDS : [])
  ];
}

function defaultOrder(
  canSeeTimeOff: boolean,
  canSeeStorage: boolean,
  canSeePharmacyTracking: boolean
): DashboardCardId[] {
  return cardsForUser(canSeeTimeOff, canSeeStorage, canSeePharmacyTracking).map((x) => x.id);
}

/** Only the cards that need no API call are on by default; role summaries stay opt-in. */
const DEFAULT_VISIBLE_CARDS: readonly DashboardCardId[] = [
  TIPS_AND_TRICKS_CARD_ID,
  MY_APPS_SHORTCUTS_CARD_ID,
  CALCULATOR_CARD_ID
];

function defaultHidden(order: readonly DashboardCardId[]): DashboardCardId[] {
  return order.filter((id) => !DEFAULT_VISIBLE_CARDS.includes(id));
}

function isStorageCardId(id: DashboardCardId): id is (typeof DEFAULT_STORAGE_CARD_ORDER)[number] {
  return DEFAULT_STORAGE_CARD_ORDER.includes(id as (typeof DEFAULT_STORAGE_CARD_ORDER)[number]);
}

export function DashboardPage() {
  const { hasAnyHrRole, hasSecurePharmDashboardAccess, hasPharmacyTrackingDashboardAccess } = useHasRole();
  const username = useAuthStore((s) => s.username);
  const canSeeTimeOffDashboard = hasAnyHrRole();
  const canSeeStorageDashboard = hasSecurePharmDashboardAccess();
  const canSeePharmacyTrackingDashboard = hasPharmacyTrackingDashboardAccess();
  const canSeeRoleDashboards =
    canSeeTimeOffDashboard || canSeeStorageDashboard || canSeePharmacyTrackingDashboard;
  const availableCards = useMemo(
    () => cardsForUser(canSeeTimeOffDashboard, canSeeStorageDashboard, canSeePharmacyTrackingDashboard),
    [canSeePharmacyTrackingDashboard, canSeeStorageDashboard, canSeeTimeOffDashboard]
  );
  const allowedIds = useMemo(
    () => defaultOrder(canSeeTimeOffDashboard, canSeeStorageDashboard, canSeePharmacyTrackingDashboard),
    [canSeePharmacyTrackingDashboard, canSeeStorageDashboard, canSeeTimeOffDashboard]
  );
  const initialHidden = useMemo(() => defaultHidden(allowedIds), [allowedIds]);

  const { layout, hidden, visibleOrder, moveCard, toggleCard, resetLayout, reorderCards, setCardWidth } =
    useDashboardLayout<DashboardCardId>(STORAGE_PREFIX, username, allowedIds, initialHidden);

  const [timeOffSummary, setTimeOffSummary] = useState<TimeOffDashboardSummary | null>(null);
  const [storageSummary, setStorageSummary] = useState<StorageDashboardSummary | null>(null);
  const [pharmacyTrackingSummary, setPharmacyTrackingSummary] =
    useState<PatientTrackingDashboardSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [customizing, setCustomizing] = useState(false);
  const [lastRefreshedAt, setLastRefreshedAt] = useState<Date | null>(null);

  const refresh = useCallback(async () => {
    if (!canSeeRoleDashboards) {
      setLastRefreshedAt(new Date());
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [timeOffResult, storageResult, pharmacyTrackingResult] = await Promise.allSettled([
        canSeeTimeOffDashboard ? fetchTimeOffDashboardSummary() : Promise.resolve(null),
        canSeeStorageDashboard ? fetchStorageDashboardSummary() : Promise.resolve(null),
        canSeePharmacyTrackingDashboard
          ? fetchPatientTrackingDashboardSummary()
          : Promise.resolve(null)
      ]);

      const errors: string[] = [];
      if (timeOffResult.status === 'fulfilled') {
        setTimeOffSummary(timeOffResult.value);
      } else if (canSeeTimeOffDashboard) {
        errors.push(
          timeOffResult.reason instanceof Error
            ? timeOffResult.reason.message
            : 'Failed to load time off dashboard summary.'
        );
      }

      if (storageResult.status === 'fulfilled') {
        setStorageSummary(storageResult.value);
      } else if (canSeeStorageDashboard) {
        errors.push(
          storageResult.reason instanceof Error
            ? storageResult.reason.message
            : 'Failed to load storage dashboard summary.'
        );
      }

      if (pharmacyTrackingResult.status === 'fulfilled') {
        setPharmacyTrackingSummary(pharmacyTrackingResult.value);
      } else if (canSeePharmacyTrackingDashboard) {
        errors.push(
          pharmacyTrackingResult.reason instanceof Error
            ? pharmacyTrackingResult.reason.message
            : 'Failed to load patient tracking dashboard summary.'
        );
      }

      setError(errors.length > 0 ? errors.join(' ') : null);
      if (errors.length === 0) {
        setLastRefreshedAt(new Date());
      }
    } finally {
      setLoading(false);
    }
  }, [
    canSeePharmacyTrackingDashboard,
    canSeeRoleDashboards,
    canSeeStorageDashboard,
    canSeeTimeOffDashboard
  ]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  function renderCard(id: DashboardCardId) {
    if (id === TIPS_AND_TRICKS_CARD_ID) {
      return <TipsAndTricksCard username={username} />;
    }

    if (id === MY_APPS_SHORTCUTS_CARD_ID) {
      return <MyAppsShortcutsCard registry={WEBOPS_MY_APPS_REGISTRY} />;
    }

    if (id === CALCULATOR_CARD_ID) {
      return <CalculatorWidget storageKey="webopsui:calculator-v1" />;
    }

    if (isStorageCardId(id)) {
      if (!storageSummary) return null;
      return renderStorageDashboardCard(id, storageSummary, {
        openHref: '/finance/secure-pharm-reports',
        openLabel: 'Open Secure Pharm Reports',
        accessTitle: 'Secure Pharm Reports',
        accessSubtitle: 'Storage mounts you can access'
      });
    }

    if (pharmacyTrackingSummary) {
      if (id === 'pharmacyOpenIssues' || id === 'pharmacyTrackerHealth' || id === 'pharmacyCeOverview') {
        return renderPatientTrackingDashboardCard(id, pharmacyTrackingSummary, {
          trackerHref: '/pharmacy/ce-patient-tracking',
          coveredEntityHref: '/pharmacy/covered-entity-list'
        });
      }
    }

    if (!timeOffSummary) return null;
    if (id === 'timeOffToday') {
      return <TimeOffTodayCard summary={timeOffSummary} openAllHref="/hr/time-off/calendar" />;
    }
    if (id === 'syncHealth') {
      return <TimeOffSyncHealthCard summary={timeOffSummary} />;
    }
    if (id === 'attention') {
      return <TimeOffAttentionCard summary={timeOffSummary} manageHref="/hr/time-off/all" />;
    }
    return null;
  }

  const needsRoleSummaries = visibleOrder.some((id) => !DEFAULT_VISIBLE_CARDS.includes(id));
  const hasLoadedSummary =
    !needsRoleSummaries ||
    ((!canSeeTimeOffDashboard || timeOffSummary) &&
      (!canSeeStorageDashboard || storageSummary) &&
      (!canSeePharmacyTrackingDashboard || pharmacyTrackingSummary));

  return (
    <div className="ops-page">
      <div className="dashboard-header">
        <div>
          <h2 className="dashboard-title">Dashboard</h2>
          <p className="dashboard-subtitle">
            Your operations snapshot for the areas you can access.
          </p>
        </div>
        <DashboardHeaderActions
          loading={loading}
          customizing={customizing}
          lastRefreshedAt={lastRefreshedAt}
          onRefresh={() => void refresh()}
          onToggleCustomize={() => setCustomizing((x) => !x)}
        />
      </div>

      {customizing ? (
        <DashboardLayoutControls
          cards={availableCards}
          order={layout.order}
          hidden={hidden}
          onMove={moveCard}
          onToggle={toggleCard}
          onReset={resetLayout}
          onReorder={(fromIndex, toIndex) => reorderCards(fromIndex, toIndex)}
        />
      ) : null}

      {error ? (
        <div className="dashboard-error" role="alert">
          {error}
        </div>
      ) : null}

      {loading && needsRoleSummaries && !hasLoadedSummary ? <DashboardLoadingState /> : null}

      {hasLoadedSummary && visibleOrder.length === 0 ? (
        <section className="dashboard-card">
          <p className="dashboard-empty">All dashboard cards are hidden. Use Customize to show at least one card.</p>
        </section>
      ) : null}

      {hasLoadedSummary && visibleOrder.length > 0 ? (
        <DashboardSortableGrid
          customizing={customizing}
          visibleOrder={visibleOrder}
          widths={layout.widths}
          onWidthChange={setCardWidth}
          renderCard={renderCard}
          onReorderVisible={(fromIndex, toIndex) =>
            reorderCards(fromIndex, toIndex, { visibleOnly: true })
          }
        />
      ) : null}
    </div>
  );
}
